/**
 * Auth Restore Utility
 * Restores a support (impersonation) session handed over from another domain.
 *
 * Login review item 3: the hand-off used to be `#auth=<base64 JSON>` carrying
 * session tokens (and the superadmin's own token), and every page load accepted
 * it -- so anyone could send a link that signed the visitor's browser into an
 * account of the sender's choosing. It is now `#handoff=<code>`: a single-use,
 * 60-second code the API trades for the session (POST
 * /v1/auth/session-handoff/redeem). Tokens never appear in the URL, and a code
 * nobody minted redeems to nothing. `#auth=` is ignored and cleared.
 *
 * Returns a promise; main.jsx renders once it settles.
 */
import { setItem } from "./storage";

const clearHash = () => {
  try {
    window.history.replaceState(null, null, window.location.pathname + window.location.search);
  } catch (e) {
    /* ignore */
  }
};

export const restoreAuthFromHash = async () => {
  try {
    const params = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
    if (params.has("auth")) {
      // Retired format: never trusted again.
      clearHash();
      return;
    }
    const code = params.get("handoff");
    if (!code) return;
    clearHash();

    const res = await fetch(`${import.meta.env.VITE_BASE_API}/v1/auth/session-handoff/redeem`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    const body = await res.json().catch(() => null);
    const data = res.ok ? body?.data : null;
    if (!data?.token) {
      sessionStorage.setItem("sessionExpired", "true");
      return;
    }

    setItem("adminToken", data.token);
    if (data.CompanyId) setItem("CompanyId", data.CompanyId);
    if (data.CompanyUrl) setItem("CompanyUrl", data.CompanyUrl);
    setItem("isImpersonated", "true");
    if (data.impersonatedBy) setItem("impersonatedBy", data.impersonatedBy);
    if (data.currentLogoPath) setItem("currentLogoPath", data.currentLogoPath);
    // Where "exit support session" returns to (the job domain). The
    // superadmin's own session stays there, in that domain's storage.
    if (data.appUrl) setItem("appUrl", data.appUrl);
  } catch (error) {
    console.error("Error restoring a handed-off session:", error);
  }
};
